import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import toast, { Toaster } from 'react-hot-toast';

const StaffLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email.trim() || !password.trim()) {
      setError('Please enter both email and password');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const responseData = await login({ email, password }, 'admin');
      console.log('FULL LOGIN RESPONSE:', responseData);
      
      if (responseData.success) {
        const staff = responseData.data?.staff;
        console.log('Staff role:', staff?.role);
        toast.success(`Welcome back, ${staff?.name}!`);
        
        // Role-based redirect
        switch (staff?.role) {
          case 'admin':
            navigate('/admin/dashboard');
            break;
          case 'teacher':
            navigate('/teacher/dashboard');
            break;
          case 'accountant':
            navigate('/accountant/dashboard');
            break;
          case 'secretary':
            navigate('/secretary/dashboard');
            break;
          default:
            navigate('/admin/dashboard');
        }
      } else {
        setError(responseData.message || 'Invalid email or password');
        toast.error(responseData.message || 'Invalid email or password');
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Something went wrong, please try again';
      if (err.response?.status === 401) {
        setError('Invalid email or password');
      } else {
        setError('Something went wrong, please try again');
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-blue-50/20 flex items-center justify-center p-4">
      <Toaster position="top-center" />

      <div className="w-full max-w-5xl grid lg:grid-cols-2 gap-0 bg-white rounded-3xl shadow-2xl overflow-hidden">
        {/* Left Side - Image/Branding */}
        <div className="hidden lg:flex flex-col justify-between bg-gradient-to-br from-indigo-700 to-indigo-900 p-10 text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-10 left-10 w-32 h-32 border-4 border-white rounded-full" />
            <div className="absolute bottom-20 right-10 w-48 h-48 border-4 border-white rounded-full" />
            <div className="absolute top-1/2 left-1/2 w-64 h-64 border-4 border-white rounded-full -translate-x-1/2 -translate-y-1/2" />
          </div>

          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                <i className="fas fa-user-shield text-2xl" />
              </div>
              <div>
                <h1 className="font-bold text-xl">Islamic School</h1>
                <p className="text-indigo-200 text-sm">Admin Portal</p>
              </div>
            </div>
          </div>

          <div className="relative z-10">
            <h2 className="text-3xl font-black mb-4 leading-tight">
              Welcome Back,<br />Administrator!
            </h2>
            <p className="text-indigo-100 text-lg leading-relaxed mb-8">
              Manage students, upload results, track attendance, and stay connected with your school community.
            </p>
          </div>

          <div className="relative z-10 text-sm text-indigo-300">
            <p>© 2026 Islamic School. All rights reserved.</p>
          </div>
        </div>

        {/* Right Side - Form */}
        <div className="p-8 lg:p-12 flex flex-col justify-center">
          <div className="mb-8">
            <h2 className="text-2xl font-black text-slate-800 mb-2">Admin Login</h2>
            <p className="text-slate-500">Enter your credentials to access the admin portal</p>
          </div>

          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
              <i className="fas fa-exclamation-circle text-red-500 mt-0.5" />
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <i className="fas fa-envelope absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@school.edu"
                  className="w-full pl-11 pr-4 py-3.5 rounded-xl border-2 border-slate-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 transition-all outline-none text-slate-800"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Password
              </label>
              <div className="relative">
                <i className="fas fa-lock absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full pl-11 pr-12 py-3.5 rounded-xl border-2 border-slate-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 transition-all outline-none text-slate-800"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`} />
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" />
                <span className="text-sm text-slate-600">Remember me</span>
              </label>
              <Link to="/auth/forgot-password" className="text-sm font-medium text-indigo-600 hover:text-indigo-700 transition-colors">
                Forgot Password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-indigo-600 to-indigo-700 text-white py-4 rounded-xl font-bold text-lg shadow-lg shadow-indigo-200 hover:shadow-xl hover:-translate-y-0.5 transition-all disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-3"
            >
              {loading ? (
                <>
                  <i className="fas fa-circle-notch fa-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  <i className="fas fa-sign-in-alt" />
                  Sign In
                </>
              )}
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-slate-100 text-center space-y-3">
            <p className="text-slate-500 text-sm">
              <Link to="/auth/student-login" className="font-semibold text-indigo-600 hover:text-indigo-700 transition-colors">
                <i className="fas fa-user-graduate mr-1" /> Student Login
              </Link>
            </p>
            <Link to="/" className="text-slate-500 hover:text-slate-700 text-sm block">
              <i className="fas fa-arrow-left mr-1" /> Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffLogin;
